import React, { useEffect, useState, useRef } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { handleOAuthCallback as dispatchOAuthCallback } from '../../store/Integration';
import './OAuthCallback.css';
import { getWorkspacePath } from '../../utils/workspaceUtils';

// Global Set to track processed OAuth codes across all component instances
const globalProcessedCodes = new Set();

// Helper to format integration name for display
const formatDisplayName = (name) => {
  if (!name) return 'Integration';
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

const OAuthCallback = () => {
  const history = useHistory();
  const location = useLocation();
  const { provider } = useParams(); // Get provider from URL params (e.g., /oauth/:provider/callback)
  const dispatch = useDispatch();

  // Get provider from URL params or fallback to detecting from path
  const getProvider = () => {
    if (provider) return provider.toLowerCase();
    // Fallback: detect from path (e.g., /integration/gmail/success)
    const path = location.pathname.toLowerCase();
    if (path.includes('gmail')) return 'gmail';
    if (path.includes('facebook')) return 'facebook';
    if (path.includes('google')) return 'gmail';
    return 'unknown';
  };

  const integrationProvider = getProvider();
  const displayName = formatDisplayName(integrationProvider);
  const messageTypePrefix = integrationProvider.toUpperCase();

  // Redux state - use dynamic provider-specific callback state
  const { loading, email: reduxEmail, error } = useSelector(state => {
    return state.Integration.oauthCallback?.[integrationProvider] || {};
  });

  const { currentWorkspace } = useSelector(state => state.Settings);
  const [email, setEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(true);
  const [isPopup, setIsPopup] = useState(false);
  const processingRef = useRef(false);

  // Detect if running in popup
  useEffect(() => {
    const inPopup = window.opener && window.opener !== window;
    setIsPopup(inPopup);
  }, []);

  // Handle Redux state changes
  useEffect(() => {
    if (reduxEmail && !loading) {
      // Success case
      console.log(`${displayName} OAuth success:`, reduxEmail);
      setEmail(reduxEmail);
      setIsProcessing(false);
      processingRef.current = false;

      if (isPopup && window.opener) {
        // Send success message to parent window
        window.opener.postMessage({
          type: `${messageTypePrefix}_OAUTH_SUCCESS`,
          email: reduxEmail
        }, window.location.origin);

        // Close popup after a short delay to show success
        setTimeout(() => window.close(), 2000);
      }
    } else if (error && !loading) {
      // Error case
      console.error(`${displayName} OAuth error:`, error);
      setIsProcessing(false);
      processingRef.current = false;

      if (isPopup && window.opener) {
        window.opener.postMessage({
          type: `${messageTypePrefix}_OAUTH_ERROR`,
          message: error
        }, window.location.origin);
        setTimeout(() => window.close(), 2000);
      } else {
        setTimeout(() => {
          history.push(getWorkspacePath('/settings/?tab=integrations', currentWorkspace?.['_id']));
        }, 3000);
      }
    }
  }, [reduxEmail, error, loading, isPopup, history, currentWorkspace, displayName, messageTypePrefix]);

  useEffect(() => {
    // Prevent duplicate processing
    if (processingRef.current) {
      console.log('OAuth callback already being processed, skipping...');
      return;
    }

    // Get query parameters from URL
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const urlError = urlParams.get('error');
    const emailParam = urlParams.get('email');

    if (urlError) {
      const errorMessage = `${displayName} connection failed: ${urlError}`;
      if (isPopup && window.opener) {
        window.opener.postMessage({
          type: `${messageTypePrefix}_OAUTH_ERROR`,
          message: errorMessage
        }, window.location.origin);
        setTimeout(() => window.close(), 1000);
      } else {
        setIsProcessing(false);
        setTimeout(() => {
          history.push(getWorkspacePath('/settings/?tab=integrations', currentWorkspace?.['_id']));
        }, 3000);
      }
      return;
    }

    if (emailParam) {
      // Email is already in URL params (from server redirect)
      setEmail(emailParam);
      setIsProcessing(false);

      if (isPopup && window.opener) {
        // Send success to parent window
        window.opener.postMessage({
          type: `${messageTypePrefix}_OAUTH_SUCCESS`,
          email: emailParam
        }, window.location.origin);
        setTimeout(() => window.close(), 2000);
      }
    } else if (code && state) {
      const cacheKey = `${code}:${state}`;
      if (globalProcessedCodes.has(cacheKey)) {
        return;
      }
      globalProcessedCodes.add(cacheKey);
      processingRef.current = true;
      handleOAuthCallbackAction(code, state);

      setTimeout(() => {
        globalProcessedCodes.delete(cacheKey);
      }, 60 * 1000);
    } else {
      const errorMessage = 'Invalid OAuth callback parameters';
      if (isPopup && window.opener) {
        window.opener.postMessage({
          type: `${messageTypePrefix}_OAUTH_ERROR`,
          message: errorMessage
        }, window.location.origin);
        setTimeout(() => window.close(), 1000);
      } else {
        setIsProcessing(false);
        setTimeout(() => {
          history.push(getWorkspacePath('/settings/?tab=integrations', currentWorkspace?.['_id']));
        }, 3000);
      }
    }
  }, [location.search, isPopup, history, currentWorkspace, displayName, messageTypePrefix]);

  const handleOAuthCallbackAction = (code, state) => {
    setIsProcessing(true);
    processingRef.current = true;

    // Dispatch generic OAuth callback handler for any provider
    dispatch(dispatchOAuthCallback(integrationProvider, code, state));
  };

  const handleGoToDashboard = () => {
    if (isPopup && window.opener) {
      window.opener.focus();
      window.close();
    } else {
      history.push(getWorkspacePath('/', currentWorkspace?._id));
    }
  };

  const handleGoToIntegrations = () => {
    if (isPopup && window.opener) {
      window.opener.focus();
      window.close();
    } else {
      history.push('/settings/?tab=integrations');
    }
  };

  const handleCloseWindow = () => {
    if (isPopup && window.opener) {
      window.close();
    } else {
      history.push('/settings/?tab=integrations');
    }
  };

  // Auto-close popup after timeout
  useEffect(() => {
    if (isPopup && !isProcessing) {
      const timer = setTimeout(() => {
        window.close();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [isPopup, isProcessing]);

  if (isProcessing || loading) {
    return (
      <div className="oauth-success-container">
        <div className="oauth-success-content processing">
          <div className="loading-spinner"></div>
          <h1>Connecting {displayName}...</h1>
          <p>Please wait while we set up your {displayName} integration.</p>
          {isPopup && <p className="popup-note">This window will close automatically when complete.</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="oauth-success-container">
      <div className="oauth-success-content">
        <div className="success-icon">
          <div className="success-checkmark">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M20 6L9 17L4 12"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        <h1>{displayName} Connected Successfully!</h1>
        <p>Your {displayName} integration has been configured and is ready to use.</p>

        {email && (
          <div className="email-badge">
            <span className="email-icon">📧</span>
            <strong>{email}</strong>
          </div>
        )}

        <p className="description">
          You can now use {displayName} tools in Sales AI for your workflows and automations.
        </p>

        {isPopup ? (
          <div className="popup-actions">
            <button className="btn-primary" onClick={handleCloseWindow}>
              <span className="btn-icon">✕</span>
              Close Window
            </button>
            <p className="footer-text">
              This window will automatically close in 5 seconds
            </p>
          </div>
        ) : (
          <div className="button-group">
            <button className="btn-primary" onClick={handleGoToDashboard}>
              <span className="btn-icon">🏠</span>
              Go to Dashboard
            </button>
            <button className="btn-secondary" onClick={handleGoToIntegrations}>
              <span className="btn-icon">⚙️</span>
              View Integrations
            </button>
            <button className="btn-ghost" onClick={handleCloseWindow}>
              <span className="btn-icon">✕</span>
              Close Window
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback;
